/**
 * External dependencies
 */
import Utility from '@naruto/naruto-models/src/core/utility';

/**
 * Internal dependencies
 */
import {
  SET_USER_INITIALIZE,
  SET_USER_LOGIN_STATE,
  SET_USER_AUTH_STATE,
  SET_USER_OAUTH_PROCESS_STATE,
  SET_USER_TOKEN_INFO
} from '@naruto/bgdcms/src/reducers/cores/user/actions';
import {
  readLocalTokenAndSave,
  saveLocalToken,
  removeLocalToken
} from '@naruto/bgdcms/src/reducers/_Capture/tokenCapture';
import { resetAllReduxStoreState } from '@naruto/bgdcms/src/reducers/_Capture/eventCapture';
import { errorCatch } from '@naruto/bgdcms/src/reducers/_Capture/errorCapture';
import { REDIRECT_PARAMS } from '@naruto/bgdcms/src/base/general';
import { Dispatch, ThunkAction, Models, MiddleWare, UserAccountLoginData, UserTokenAccountInfo } from './types';

/* Initial */
export const posUserInitialize =
  (): ThunkAction<Promise<void>, Models, MiddleWare, { type: string }> =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(SET_USER_INITIALIZE());
  };

/* User Action */
export const postUserLoginState =
  (isLogin: boolean): ThunkAction<Promise<void>, Models, MiddleWare, { type: string }> =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(
      SET_USER_LOGIN_STATE({
        isLogin
      })
    );
  };
export const postUserAuthState =
  (state: boolean): ThunkAction<Promise<void>, Models, MiddleWare, { type: string }> =>
  async (dispatch): Promise<void> => {
    dispatch(SET_USER_AUTH_STATE({ isAuth: state }));
  };
export const postUserAuthProcessState =
  (state: boolean): ThunkAction<Promise<void>, Models, MiddleWare, { type: string }> =>
  async (dispatch): Promise<void> => {
    dispatch(SET_USER_OAUTH_PROCESS_STATE({ isSuccess: state }));
  };
export const getUserIsLogin =
  (): ThunkAction<Promise<void>, Models, MiddleWare, { type: string }> =>
  async (dispatch, getState, { api }): Promise<void> => {
    const utility = new Utility();
    const savedToken = await dispatch(readLocalTokenAndSave());
    /* 有儲存 token 在本地 */
    if (savedToken) {
      const result = await api.user.getUserToken<UserTokenAccountInfo>();
      /* token 有效，且是登入狀態 */
      if (!result.error && result.response) {
        dispatch(
          SET_USER_TOKEN_INFO({
            info: result.response.account
          })
        );
        await dispatch(postUserLoginState(true));
      }
      /* token 無效 */
      if (result.error) {
        await dispatch(removeLocalToken());
      }
    }
    /* 沒有儲存 token 在本地 */
    await utility.delay(800);
    await dispatch(postUserAuthState(true));
  };
export const getUserToken =
  (): ThunkAction<Promise<void>, Models, MiddleWare, { type: string }> =>
  async (dispatch, getState, { api }): Promise<void> => {
    const result = await api.user.getUserToken<UserTokenAccountInfo>();
    if (!result.error && result.response) {
      dispatch(
        SET_USER_TOKEN_INFO({
          info: result.response.account
        })
      );
    } else {
      const oldState = getState().user.userTokenInfo;
      dispatch(SET_USER_TOKEN_INFO({ info: { ...oldState.account } }));
    }
    await dispatch(errorCatch(result.error, result.status));
  };
export const postUserLogin =
  (loginData: UserAccountLoginData): ThunkAction<Promise<void>, Models, MiddleWare, { type: string }> =>
  async (dispatch, getState, { api }): Promise<void> => {
    const result = await api.user.postUserLogin<UserTokenAccountInfo, UserAccountLoginData>(loginData);
    if (!result.error && result.response) {
      await dispatch(saveLocalToken(result.response.token as string));
      dispatch(SET_USER_TOKEN_INFO({ info: result.response.account }));
      const query = new URLSearchParams(window.location.search);
      const redirectURl = query.get(REDIRECT_PARAMS);
      await dispatch(postUserLoginState(true));
      if (redirectURl) {
        const newURL = new URL(window.location.pathname, window.location.origin);
        newURL.searchParams.set(REDIRECT_PARAMS, redirectURl);
        window.history.pushState({}, '', newURL.toString());
      }
    } else {
      const oldState = getState().user.userTokenInfo;
      dispatch(SET_USER_TOKEN_INFO({ info: { ...oldState.account } }));
    }
    await dispatch(errorCatch(result.error, result.status, undefined, false));
  };
export const postUserLogout =
  (): ThunkAction<Promise<void>, Models, MiddleWare, { type: string }> =>
  async (dispatch): Promise<void> => {
    await dispatch(resetAllReduxStoreState());
    await dispatch(removeLocalToken());
  };
